import { useCallback, useEffect, useMemo } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';

import usePrevious from 'lib/src/hooks/usePrevious';
import useForm from '@components/form/hooks/useForm';

import { useHistory } from 'react-router-dom';
import {
    getChallengeGroupsIsPosting,
    getChallengeGroupsPostError,
    getChallengeGroupsPostSuccess,
    getChallengeGroup,
    getChallengeGroupAdmins,
    getChallengeGroupViewers,
} from '@selectors/challengeGroups';
import { editChallengeGroup } from '@actions/challengeGroups/editChallengeGroup';
import { RootState } from '@reducers/index';
import {
    populateChallengeGroupAdmins,
    populateChallengeGroupViewers,
} from '@actions/challengeGroups';
import { site } from 'lib/src/shared/enums/site';
import useSite from 'lib/src/hooks/useSite';

export default function useEditChallengeGroup(id: number) {
    const dispatch = useDispatch();
    const history = useHistory();
    const challengeGroup = useSelector((state: RootState) => getChallengeGroup(state, id));

    const closeModal = useCallback(() => {
        history.push(`/challenge-groups`);
    }, [history]);

    const userSite = useSite();

    const initialSiteValue = useMemo(() => {
        if (userSite === site.ALL && challengeGroup.site === site.ALL) {
            return null;
        }

        if (userSite !== site.ALL && challengeGroup.site === site.ALL) {
            return userSite;
        }

        return challengeGroup.site;
    }, [challengeGroup.site, userSite]);

    const [formState, handleChange] = useForm<FormState>({
        name: challengeGroup.name,
        viewerIDs: challengeGroup.permittedViewers.map(user => user.id),
        adminIDs: challengeGroup.permittedAdmins.map(user => user.id),
        site: initialSiteValue,
    });

    const handleSubmit = useCallback(() => {
        const postBody = { ...formState, site: formState.site || challengeGroup.site };

        dispatch(editChallengeGroup(challengeGroup.id, postBody));
    }, [dispatch, challengeGroup, formState]);

    const isPosting = useSelector(getChallengeGroupsIsPosting);
    const error = useSelector(getChallengeGroupsPostError);
    const postSuccess = useSelector(getChallengeGroupsPostSuccess);
    const prevPostSuccess = usePrevious(postSuccess);

    const admins = useSelector(getChallengeGroupAdmins);
    const viewers = useSelector(getChallengeGroupViewers);

    const adminOptions = useMemo(
        () =>
            admins
                .filter(user => user.site === 1000 || user.site === challengeGroup.site)
                .sort((a, b) => a.lastName.localeCompare(b.lastName))
                .map(user => ({ value: user.id, label: `${user.firstName} ${user.lastName}` })),

        [admins, challengeGroup],
    );

    const viewerOptions = useMemo(
        () =>
            viewers
                .filter(user => user.site === 1000 || user.site === challengeGroup.site)
                .sort((a, b) => a.lastName.localeCompare(b.lastName))
                .map(user => ({ value: user.id, label: `${user.firstName} ${user.lastName}` })),

        [viewers, challengeGroup],
    );

    useEffect(() => {
        batch(() => {
            dispatch(populateChallengeGroupAdmins(id));
            dispatch(populateChallengeGroupViewers(id));
        });
    }, [id, dispatch]);

    useEffect(() => {
        if (!prevPostSuccess && postSuccess) {
            closeModal();
        }
    }, [postSuccess, prevPostSuccess, closeModal]);

    return {
        handleSubmit,
        formState,
        handleChange,
        closeModal,
        isPosting,
        error,
        challengeGroup,
        adminOptions,
        viewerOptions,
    };
}

interface FormState {
    name: string;
    viewerIDs: number[];
    adminIDs: number[];
    site: site | null;
}
